<template>
    <div id="list-suppliers-component" class="w-full h-full">
        <div class="flex items-end justify-between gap-2">
            <span v-if="isLoading"> Cargando...</span>
            <custom-simple-select
                v-else
                v-model="option"
                label="Proveedor"
                mode="search"
                class="w-full"
                :options="selectOptions"
                :disabled="!enabled"
                @change="onInput"
            />
            <app-button
                v-if="enabled"
                icon
                circle
                color="brand-purple"
                @click="reloadItems(true)"
            >
                <reload-icon class="h-5 w-5 text-white" />
            </app-button>
            <app-button
                v-if="enabled && allowCreate"
                icon
                circle
                color="brand-purple"
                title="Crear proveedor"
                @click="openCreateModal"
            >
                <span class="h-5 w-5 font-bold text-lg leading-none">+</span>
            </app-button>
        </div>

        <app-dialog v-model="showCreate" size="lg">
            <template #header>
                <h2 class="text-theme-primary text-2xl">Crear proveedor</h2>
            </template>
            <div class="flex flex-wrap p-2">
                <div class="w-full md:w-1/2 mt-4 px-2">
                    <custom-input
                        v-model="newSupplier.name"
                        class="w-full"
                        label="Nombre *"
                    />
                </div>
                <div class="w-full md:w-1/2 mt-4 px-2">
                    <custom-input
                        v-model="newSupplier.phone"
                        class="w-full"
                        label="Celular de contacto"
                        type="number"
                    />
                </div>
                <div class="w-full mt-4 px-2">
                    <custom-checkbox v-model="showMoreFields">
                        Agregar más datos
                    </custom-checkbox>
                </div>
            </div>
            <div v-if="showMoreFields" class="flex flex-wrap p-2">
                <div class="w-full md:w-1/2 mt-4 px-2">
                    <identification-type-selector
                        v-model="newSupplier.identificationType"
                        :enabled="true"
                    />
                </div>
                <div class="w-full md:w-1/2 mt-4 px-2">
                    <custom-input
                        v-model="newSupplier.identificationNumber"
                        class="w-full"
                        label="Número de identificación"
                    />
                </div>
                <div class="w-full md:w-1/2 mt-4 px-2">
                    <city-code-selector
                        v-model="newSupplier.cityCode"
                        :enabled="true"
                    />
                </div>
                <div class="w-full md:w-1/2 mt-4 px-2">
                    <custom-input
                        v-model="newSupplier.address"
                        class="w-full"
                        label="Dirección Ej. Cll 42 # 12 - 8"
                        placeholder="Cll 42 # 12 - 8"
                    />
                </div>
                <div class="w-full md:w-1/2 mt-4 px-2">
                    <custom-input
                        v-model="newSupplier.addressComplement"
                        class="w-full"
                        label="Complemento de Dirección Ej. Local 123"
                        placeholder="Local 123"
                    />
                </div>
                <div class="w-full md:w-1/2 mt-4 px-2">
                    <custom-input
                        v-model="newSupplier.neighborhood"
                        class="w-full"
                        label="Barrio / Localidad"
                    />
                </div>
                <div class="w-full md:w-1/2 mt-4 px-2">
                    <custom-input
                        v-model="newSupplier.coordinates"
                        class="w-full"
                        label="Coordenadas Ej. 4.6097,-74.0817"
                        placeholder="4.6097,-74.0817"
                    />
                </div>
            </div>
            <template #footer>
                <div class="flex justify-center my-2">
                    <app-button :disabled="isCreating" @click="createNew">
                        {{ isCreating ? "Creando..." : "Crear proveedor" }}
                    </app-button>
                </div>
            </template>
        </app-dialog>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import { removeEmptyFields } from "@/common/lib/transformers";

import ReloadIcon from "@/components/icons/ReloadIcon.vue";
import IdentificationTypeSelector from "@/components/users/IdentificationTypeSelector.vue";
import AppDialog from "@/components/utils/AppDialog.vue";
import CityCodeSelector from "@/components/utils/CityCodeSelector.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import CustomSimpleSelect from "@/components/utils/CustomSimpleSelect.vue";

export default {
    name: "SupplierSelector",
    components: {
        AppDialog,
        ReloadIcon,
        CityCodeSelector,
        IdentificationTypeSelector,
        CustomInput,
        CustomSimpleSelect
    },
    model: {
        prop: "itemCurrent",
        event: "change"
    },
    props: {
        itemCurrent: {
            default: () => ({
                name: "indefinido",
                slug: ""
            }),
            type: Object,
            require: true
        },
        enabled: {
            default: () => true,
            type: Boolean,
            required: false
        },
        fill: {
            default: () => false,
            type: Boolean,
            required: false
        },
        allowCreate: {
            default: () => false,
            type: Boolean,
            required: false
        }
    },
    emits: ["change"],
    data: () => ({
        option: "",
        options: [],
        isLoading: true,
        showCreate: false,
        isCreating: false,
        showMoreFields: false,
        newSupplier: {
            name: "",
            phone: "",
            identificationType: "",
            identificationNumber: "",
            address: "",
            addressComplement: "",
            cityCode: "",
            neighborhood: "",
            coordinates: ""
        }
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        selectOptions() {
            return this.options.map((item) => ({
                label: item.name,
                value: item.id
            }));
        }
    },
    watch: {
        itemCurrent(value) {
            this.setInput(value);
        }
    },
    mounted() {
        this.setInput(this.itemCurrent);
        if (this.fill) {
            this.reloadItems(true);
        }
    },
    methods: {
        ...mapActions("commons", ["listSupplierAll"]),
        ...mapActions("suppliers", ["createSupplier"]),
        async reloadItems(required = true) {
            this.isLoading = true;
            try {
                this.options = await this.listSupplierAll({ required });
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        setInput(value) {
            const aux = this.options.find((item) => item.id === value.id);
            if (!aux) {
                this.options = [value];
            }
            this.option = value.id ?? "";
            this.isLoading = false;
        },
        onInput() {
            const aux = this.options.find((item) => this.option === item.id);
            if (aux) {
                this.$emit("change", aux);
            }
        },
        openCreateModal() {
            this.newSupplier = {
                name: "",
                phone: "",
                identificationType: "",
                identificationNumber: "",
                address: "",
                addressComplement: "",
                cityCode: "",
                neighborhood: "",
                coordinates: ""
            };
            this.showMoreFields = false;
            this.showCreate = true;
        },
        async createNew() {
            if (!this.newSupplier.name.trim()) return;
            this.isCreating = true;
            try {
                const { name, phone } = this.newSupplier;
                const created = await this.createSupplier(
                    removeEmptyFields(
                        this.showMoreFields
                            ? { ...this.newSupplier }
                            : { name, phone }
                    )
                );
                await this.reloadItems(true);
                this.option = created.id;
                this.$emit("change", created);
                this.showCreate = false;
            } catch (error) {
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            } finally {
                this.isCreating = false;
            }
        }
    }
};
</script>
